import type { NudgeQueueSummary } from '../../types/api'
import type { NudgeStatus } from '../../types/enums'

// ─── Status groups (single source of truth) ───────────────────────────────────

export const SENT_STATUSES: NudgeStatus[] = [
  'SENT', 'REPLY_RECEIVED', 'CONV_ACTIVE',
  'RECOVERED', 'RETURNED', 'NO_SHOW', 'OPT_OUT', 'EXHAUSTED',
]
export const RECOVERED_STATUSES: NudgeStatus[] = ['RECOVERED', 'RETURNED']
export const ACTIVE_CONV_STATUSES: NudgeStatus[] = ['REPLY_RECEIVED', 'CONV_ACTIVE']

// ─── Metrics shape ────────────────────────────────────────────────────────────

export interface NudgeMetrics {
  // Row-based counts (email / nudge volume)
  totalQueued: number
  totalSent: number
  totalReplied: number
  totalRecovered: number       // RECOVERED + RETURNED combined
  totalRecoveredOnly: number   // RECOVERED only (awaiting owner visit confirmation)
  totalReturnedOnly: number    // RETURNED only (visit logged — fully done)
  totalNoShow: number
  totalRecoveredAll: number    // RECOVERED + RETURNED + NO_SHOW (ever reached recovery stage)
  totalOptOut: number
  totalExhausted: number
  totalExhaustedNoReply: number
  totalFailed: number
  activeConvs: number
  awaitingReply: number

  // Contact-based funnel (deduplicates contacts with multiple nudge rows)
  funnelQueued: number
  funnelSent: number
  funnelReplied: number
  funnelRecovered: number

  // Derived rates — always contact-based so every page agrees
  recoveryRatePct: number
  replyRatePct: number
  optOutRatePct: number
}

// ─── Computation ──────────────────────────────────────────────────────────────

export function computeNudgeMetrics(rows: NudgeQueueSummary[]): NudgeMetrics {
  const count = (s: NudgeStatus) => rows.filter(r => r.status === s).length
  const countAny = (ss: NudgeStatus[]) => rows.filter(r => ss.includes(r.status)).length

  const totalQueued           = rows.length
  const totalSent             = countAny(SENT_STATUSES)
  const totalReplied          = rows.filter(r => r.replyReceivedAt != null).length
  const totalRecovered        = countAny(RECOVERED_STATUSES)
  const totalRecoveredOnly    = count('RECOVERED')
  const totalReturnedOnly     = count('RETURNED')
  const totalNoShow           = count('NO_SHOW')
  const totalRecoveredAll     = totalRecoveredOnly + totalReturnedOnly + totalNoShow
  const totalOptOut           = count('OPT_OUT')
  const totalExhausted        = count('EXHAUSTED')
  const totalExhaustedNoReply = rows.filter(r => r.status === 'EXHAUSTED' && r.replyReceivedAt == null).length
  const totalFailed           = count('FAILED')
  const activeConvs           = countAny(ACTIVE_CONV_STATUSES)
  const awaitingReply         = count('SENT')

  // Contact-based funnel: a customer with Nudge #1 + Nudge #2 is one contact
  const funnelQueued    = new Set(rows.map(r => r.contactId)).size
  const funnelSent      = new Set(rows.filter(r => SENT_STATUSES.includes(r.status)).map(r => r.contactId)).size
  const funnelReplied   = new Set(rows.filter(r => r.replyReceivedAt != null).map(r => r.contactId)).size
  const funnelRecovered = new Set(rows.filter(r => RECOVERED_STATUSES.includes(r.status)).map(r => r.contactId)).size
  const funnelOptOut    = new Set(rows.filter(r => r.status === 'OPT_OUT').map(r => r.contactId)).size

  const recoveryRatePct = funnelSent > 0 ? Math.round((funnelRecovered / funnelSent) * 100) : 0
  const replyRatePct    = funnelSent > 0 ? Math.round((funnelReplied   / funnelSent) * 100) : 0
  const optOutRatePct   = funnelSent > 0 ? Math.round((funnelOptOut    / funnelSent) * 100) : 0

  return {
    totalQueued, totalSent, totalReplied, totalRecovered,
    totalRecoveredOnly, totalReturnedOnly, totalNoShow, totalRecoveredAll,
    totalOptOut, totalExhausted, totalExhaustedNoReply, totalFailed,
    activeConvs, awaitingReply,
    funnelQueued, funnelSent, funnelReplied, funnelRecovered,
    recoveryRatePct, replyRatePct, optOutRatePct,
  }
}
